import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";

import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {Difference} from "../models/difference";
import {JournalStore} from "./journal-store-mobx";
import {ProcessSource, ProcessSourceProps} from "../models/process-source";
import {Project} from "../models/project";

export interface ProcessSourceStoreProps {
    layerConfig: ApiLayerConfiguration;
    journalStore: JournalStore;
}

export class ProcessSourceStore extends AbstractStore {

    journalStore: JournalStore | undefined;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ProcessSource>();

    constructor(props: ProcessSourceStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.journalStore = props.journalStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): ProcessSource[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        // filtered.sort(ProcessSource.sortByName);
        return [ ...filtered];
    }

    loadItem(guid: string): Promise<ProcessSource | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            // !! do not use loadItems here. Then it wouldn't be possible to sync the
            // observed properties without swapping the whole items!!
            this.queryFeatures(undefined, [guid]).then((processSources: ProcessSource[]) => {
                if (processSources[0] && processSources[0].guid === guid) {
                    const processSource = processSources[0];
                    const localProcessSource = this.items.get(guid);
                    if (localProcessSource) {
                        // return process source as long as merging process is running. process source can be displayed with
                        // indicator that syncing is in process
                        // TODO: merge process source with already loaded process source
                    }
                    this.addItem(processSource);
                    this.setSelectedItem(processSource);
                    this.setDataStatus("loaded");

                    resolve(processSource);
                } else {
                    reject();
                    throw new Error("Process source not found");
                }
            }).catch((error) => {
                console.error(error);
                this.setDataStatus("error");
                throw new Error("Error loading process source");
            });
        });
    }

    /**
     * Request all process sources from server and store in local store
     *
     * @param projectId
     * @param guids (Optional) IDs of process sources
     */
    loadItems(projectId?: number, guids?: string[]): Promise<ProcessSource[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(projectId, guids).then((processSources: ProcessSource[]) => {
                    if (processSources) {
                        // do only save to mobx when not requested with specified ids or contacts
                        const map = new Map<string, ProcessSource>();
                        processSources.forEach((es: ProcessSource) => {
                            map.set(es.guid, es);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(processSources);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting process source.");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param projectId GUID of the related project
     * @param user
     */
    newItem(projectId: number | undefined, user: string): Promise<ProcessSource | null> {
        return new Promise((resolve, reject) => {

            if (!projectId) {
                reject();
            }

            // generate random id
            const randomIds = new Uint8Array(1);
            self.crypto.getRandomValues(randomIds);

            const processSource = new ProcessSource({
                //guid: crypto.randomUUID(),
                projectId,
                creator: user
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // TODO: find out why the feature template prototype does not return attributes
                processSource.feature = this.createFeature(null, processSource);

                // workaround: add missing prototype attributes
                ProcessSource.prototypeAttributes.forEach((attrName) => {
                    if (!processSource.feature.attributes[attrName]) {
                        processSource.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(processSource);
                this.setSelectedItem(processSource);

                resolve(processSource);
            });
        });
    }

    /**
     * Save process source to the server
     *
     * @param processSource
     * @param updated
     * @param user
     */
    save(processSource: ProcessSource | null,
         updated: any,
         user: string): Promise<ProcessSource | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!processSource?.guid) {
                    reject("Process source GUID undefined when saving edits.");
                    return;
                }

                if (processSource.isNewRecord) {
                    processSource.feature = this.createFeature(null, processSource);

                    // log creation in journal
                    this.journalStore?.journalizeItem(
                        processSource,
                        "process-source",
                        "add",
                        user,
                        [],
                        "Neue Prozessquelle"
                    );
                }

                if(!processSource.isNewRecord) {
                    // log changes to journal
                    let diffs: Difference[] = [];
                    diffs = this.compareAttributes(processSource, updated, ProcessSource.comparableAttributes);

                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        processSource,
                        "process-source",
                        "update",
                        user,
                        diffs,
                        "Prozessquelle geändert"
                    );
                }

                const feature = processSource?.feature;
                if (!feature) {
                    reject(`Feature undefined for process source ${processSource.objectId}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = ProcessSource.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            reject();
                            throw new Error(`Database field not found for process source ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = processSource.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(processSource, attrs);
                    if (processSource.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        processSource.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(processSource);
                }, (error) => {
                    console.error("Error saving process source", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete a process source
     *
     * @param project
     * @param processSource
     * @param user
     */
    delete(project: Project,
           processSource: ProcessSource | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!project) {
                    reject("Activity undefined when saving process source edits.");
                    return;
                }
                if (!processSource) {
                    reject("Process source undefined when saving edits.");
                    return;
                }

                if (processSource.isNewRecord) {
                    reject("This process source is not saved. Does not have to be deleted.");
                    return;
                }

                // do not allow deleting when process is further than a specified step
                if (!project.isAlterable) {
                    reject("This project cannot be changed. Process is running.");
                    return;
                }

                const feature = processSource?.feature;
                if (!feature) {
                    reject(`Feature undefined for process source ${processSource.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {
                    this.resetSelectedItem();
                    this.removeItem(processSource);

                    // log deletion in journal
                    const nameDiff = new Difference({p: "name", o: processSource.name || ""});
                    const idDiff = new Difference({p: "guid", o: processSource.guid});
                    this.journalStore?.journalizeItem(
                        project,
                        "process-source",
                        "delete",
                        user,
                        [idDiff, nameDiff],
                        "Prozessquelle gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectId?: number, processSourceGuids?: string[]): Promise<ProcessSource[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                if (projectId !== undefined) {
                    queryParams.where += ` AND PROJ_ID = ${projectId}`;
                }
                // load all features if no project ID is specified
                if (processSourceGuids !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND GUID IN ('${processSourceGuids.join("','")}')`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const processSources = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(processSources);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying project + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): ProcessSource[] {

        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const processSources: ProcessSource[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, ProcessSource);
            const processSource = new ProcessSource(json as ProcessSourceProps);
            processSource.feature = feature;

            processSources.push(processSource);
        });
        return processSources;
    }

}
